import React, { useState } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import {
  animations,
  colors,
  spaces,
  text,
  mixins,
} from '../../../core/styles/theme/default';
import ErrorMessage from '../ErrorMessage';

const StyledLabel = styled.label`
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 0;

  & + & {
    padding-left: ${rem(spaces.iota)};
  }

  &:hover {
    > span:before {
      border: 1px solid ${colors.primary};
    }
  }
`;
const StyledSpan = styled.span`
  ${text.p3};
  position: relative;
  display: flex;
  margin: 0;
  font-weight: 400;
  align-items: center;
  cursor: pointer;
  padding-left: ${rem(spaces.zeta)};

  &::before,
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: ${rem(24)};
    height: ${rem(24)};
    border-radius: ${rem(4)};
  }

  &:before {
    z-index: 2;
    border: 1px solid ${colors.grue600};
  }

  &:after {
    z-index: 3;
    transition: opacity ${animations.default};
    opacity: 0;
    background: ${colors.primary}
      url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15' height='12' fill='none'%3E%3Cpath fill='%23fff' d='M4.707 8.293L1.414 5 0 6.414l4.707 4.707 9.707-9.707L13 0z'/%3E%3C/svg%3E")
      no-repeat 5px 6px;
  }
`;
const StyledCheckbox = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  width: ${rem(24)};
  height: ${rem(24)};

  &:focus + span {
    ${mixins.fakeFocus};
  }
  &:checked + {
    &::after {
      opacity: 1;
    }
  }
  &:disabled + {
    &::before {
      background-color: ${colors.grey300};
      border-color: ${colors.grey500};
    }
  }
`;

const Checkbox = ({
  label,
  isChecked,
  isRequired,
  isDisabled,
  errorText,
  onChangeRequest,
  ...rest
}: CheckboxProps) => {
  const [isSelected, setIsSelected] = useState(isChecked || false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSelected(event.target.checked);
    onChangeRequest?.(event);
  };

  return (
    <StyledLabel>
      <StyledCheckbox
        type="checkbox"
        checked={isSelected}
        disabled={isDisabled}
        onChange={handleChange}
        required={isRequired}
        {...rest}
      />
      <StyledSpan>{label}</StyledSpan>
      {errorText && <ErrorMessage text={errorText} />}
    </StyledLabel>
  );
};

export type CheckboxProps = {
  /** Label of the Checkbox */
  label: string;
  /** Value of the Checkbox */
  value: string;
  /** Name of the Checkbox */
  name: string;
  /** Set if Checkbox is initially checked */
  isChecked?: boolean;
  /** Set if Checkbox is required */
  isRequired?: boolean;
  /** Set if Checkbox is disabled */
  isDisabled?: boolean;
  /** Error Text to be shown */
  errorText?: string;
  /** Optional Callback fired when the Checkbox changes. For example usefull to get the value of the Checkbox */
  onChangeRequest?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export default Checkbox;
